import { Link, useLocation, useNavigate } from "react-router-dom";
import { Navbar, Nav, Container, Dropdown, Badge, Modal, Button, Form } from "react-bootstrap";
import "../../styles/header.css"; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope, faTachometerAlt, faHome, faPlusCircle, faSignOutAlt,
  faChevronDown, faKey, faUserCircle
} from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect } from "react";
import { useLanguage } from "../../i18n/LanguageContext";

function Header() {
  const { t, lang, switchLang } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [role, setRole] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [cpOldPw, setCpOldPw] = useState("");
  const [cpNewPw, setCpNewPw] = useState("");
  const [cpConfirmPw, setCpConfirmPw] = useState("");
  const [cpLoading, setCpLoading] = useState(false);
  const [cpError, setCpError] = useState("");
  const [cpSuccess, setCpSuccess] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedRole = localStorage.getItem("role");
    const fn = localStorage.getItem("firstname") || "";
    const ln = localStorage.getItem("lastname") || "";
    const em = localStorage.getItem("rememberedEmail") || "";
    setIsLoggedIn(!!token);
    setRole(savedRole);
    setFirstName(fn);
    setLastName(ln);
    setUserEmail(em);
    
    if (token) {
      fetchUnreadCount();
      
      // Poll for unread count every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [location]);

  const fetchUnreadCount = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch("/api/inquiries/unread-count", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("rememberedEmail");
    localStorage.removeItem("role");
    localStorage.removeItem("firstname");
    localStorage.removeItem("lastname");
    setIsLoggedIn(false);
    setShowLogoutModal(false);
    navigate("/login");
  };

  const handleChangePassword = async () => {
    setCpError("");
    setCpSuccess("");
    if (!cpOldPw || !cpNewPw || !cpConfirmPw) { setCpError(t('modal.fillAllFields')); return; }
    if (cpNewPw !== cpConfirmPw) { setCpError(t('modal.passwordsMustMatch')); return; }
    if (cpNewPw.length < 8) { setCpError(t('modal.passwordTooShort')); return; }
    setCpLoading(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ oldPassword: cpOldPw, newPassword: cpNewPw }),
      });
      const data = await res.json();
      if (res.ok) {
        setCpSuccess(t('modal.passwordChanged'));
        setCpOldPw(""); setCpNewPw(""); setCpConfirmPw("");
      } else {
        setCpError(data.message || t('modal.networkError'));
      }
    } catch {
      setCpError(t('modal.networkError'));
    } finally {
      setCpLoading(false);
    }
  };

  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "?";
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "My Account";

  const dashboardPath = role === "owner"? "/owner-dashboard" : "/renter-dashboard";
  return (
    <>
    <Navbar bg="light" expand="lg" className="header-navbar">
      <Container fluid className="position-relative">
        <Navbar.Brand as={Link} to="/" className="d-flex align-items-center order-0 brand-block">
          <img src="/icon.png" alt="GharBhada icon" className="brand-icon" />
          <span className="ms-2 brand-copy">
            <strong>GharBhada</strong>
            <small>Rental Homes in Nepal</small>
          </span>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="navbar-nav" className="order-2 order-lg-3 ms-auto"/>

        <Navbar.Collapse id="navbar-nav" className="order-2 order-lg-2 justify-content-center">
          <Nav className="mx-auto gap-2">
            <Nav.Link as={Link} to="/find-property">{t('nav.findHome')}</Nav.Link>
            {(!isLoggedIn || role ==="owner") && (<Nav.Link as={Link} to="/list-property">{t('nav.listProperty')}</Nav.Link>)}
            <Nav.Link as={Link} to="/about">{lang === "ne" ? "हाम्रो बारेमा" : "About"}</Nav.Link>
            {isLoggedIn && (<Nav.Link as={Link} to={dashboardPath}>{t('nav.dashboard')}</Nav.Link>)}
            {isLoggedIn && (
              <Nav.Link as={Link} to="/messages" className="position-relative">
                <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                {t('nav.messages')}
                {unreadCount > 0 && (
                  <Badge 
                    bg="danger" 
                    pill 
                    className="position-absolute top-0 start-100 translate-middle"
                    style={{ fontSize: '0.7rem' }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Badge>
                )}
              </Nav.Link>
            )}
          </Nav>
            {!isLoggedIn ? (
              <>
                <Nav.Link as={Link} to="/login">
                  <button className="btn btn-outline-primary my-2 my-sm-0" type="submit">{t('nav.login')}</button>
                </Nav.Link>
                <Nav.Link as={Link} to="/signup">
                  <button className="btn btn-primary my-2 my-sm-0" type="submit">{t('nav.signUp')}</button>
                </Nav.Link>
              </>
            ):
          (
            <Nav.Link>
              <Dropdown align="end">
                <Dropdown.Toggle as="div" className="account-toggle" bsPrefix="account-toggle">
                  <div className="account-avatar">{initials}</div>
                  <span className="account-name d-none d-lg-inline">{firstName || t('nav.login')}</span>
                  <FontAwesomeIcon icon={faChevronDown} className="account-chevron d-none d-lg-inline" />
                </Dropdown.Toggle>

                <Dropdown.Menu className="account-dropdown-menu">
                  {/* User info header */}
                  <div className="account-dropdown-header">
                    <div className="account-dropdown-avatar">{initials}</div>
                    <div className="account-dropdown-info">
                      <div className="account-dropdown-name">{fullName}</div>
                      {userEmail && <div className="account-dropdown-email">{userEmail}</div>}
                      <span className={`account-role-badge ${role === "owner" ? "owner" : "renter"}`}>
                        <FontAwesomeIcon icon={role === "owner" ? faPlusCircle : faHome} className="me-1" />
                        {role === "owner" ? t('nav.propertyOwner') : t('nav.renter')}
                      </span>
                    </div>
                  </div>

                  <Dropdown.Divider />

                  {/* Navigation */}
                  <Dropdown.Item as={Link} to={dashboardPath} className="account-dropdown-item">
                    <FontAwesomeIcon icon={faTachometerAlt} className="dropdown-item-icon" />
                    {t('nav.dashboard')}
                  </Dropdown.Item>

                  <Dropdown.Item as={Link} to="/profile" className="account-dropdown-item">
                    <FontAwesomeIcon icon={faUserCircle} className="dropdown-item-icon" />
                    My Profile
                  </Dropdown.Item>

                  <Dropdown.Item as={Link} to="/messages" className="account-dropdown-item">
                    <FontAwesomeIcon icon={faEnvelope} className="dropdown-item-icon" />
                    {t('nav.messages')}
                    {unreadCount > 0 && (
                      <span className="dropdown-badge ms-auto">{unreadCount > 99 ? "99+" : unreadCount}</span>
                    )}
                  </Dropdown.Item>

                  {role === "owner" && (
                    <Dropdown.Item as={Link} to="/list-property" className="account-dropdown-item">
                      <FontAwesomeIcon icon={faPlusCircle} className="dropdown-item-icon" />
                      {t('nav.listAProperty')}
                    </Dropdown.Item>
                  )}

                  {role === "renter" && (
                    <>
                      <Dropdown.Item as={Link} to="/find-property" className="account-dropdown-item">
                        <FontAwesomeIcon icon={faHome} className="dropdown-item-icon" />
                        {t('nav.findAHome')}
                      </Dropdown.Item>
                    </>
                  )}

                  <Dropdown.Divider />

                  {/* Account actions */}
                  <Dropdown.Item
                    className="account-dropdown-item"
                    onClick={() => { setCpError(""); setCpSuccess(""); setShowChangePasswordModal(true); }}
                  >
                    <FontAwesomeIcon icon={faKey} className="dropdown-item-icon" />
                    {t('nav.changePassword')}
                  </Dropdown.Item>

                  <Dropdown.Divider />

                  <Dropdown.Item
                    className="account-dropdown-item logout-item"
                    onClick={() => setShowLogoutModal(true)}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="dropdown-item-icon" />
                    {t('nav.signOut')}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav.Link>
          )}

          {/* Language Toggle */}
          <div className="lang-toggle ms-2">
            <button
              className={`lang-btn${lang === 'en' ? ' active' : ''}`}
              onClick={() => switchLang('en')}
              aria-label="Switch to English"
            >EN</button>
            <button
              className={`lang-btn${lang === 'ne' ? ' active' : ''}`}
              onClick={() => switchLang('ne')}
              aria-label="नेपालीमा बदल्नुहोस्"
            >नेपाली</button>
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>

    {/* Logout confirmation modal */}
    <Modal show={showLogoutModal} onHide={() => setShowLogoutModal(false)} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-bold">{t('modal.signOut')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{t('modal.signOutConfirm')}</Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={() => setShowLogoutModal(false)}>{t('modal.cancel')}</Button>
        <Button variant="danger" size="sm" onClick={handleLogout}>
          <FontAwesomeIcon icon={faSignOutAlt} className="me-1" /> {t('modal.signOut')}
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Change Password modal */}
    <Modal show={showChangePasswordModal} onHide={() => setShowChangePasswordModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-bold">
          <FontAwesomeIcon icon={faKey} className="me-2 text-primary" />
          {t('modal.changePassword')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cpError && <div className="alert alert-danger py-2 small">{cpError}</div>}
        {cpSuccess && <div className="alert alert-success py-2 small">{cpSuccess}</div>}
        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">{t('modal.currentPassword')}</Form.Label>
          <Form.Control type="password" value={cpOldPw} onChange={(e) => setCpOldPw(e.target.value)} placeholder={t('modal.currentPasswordPlaceholder')} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">{t('modal.newPassword')}</Form.Label>
          <Form.Control type="password" value={cpNewPw} onChange={(e) => setCpNewPw(e.target.value)} placeholder={t('modal.newPasswordPlaceholder')} />
        </Form.Group>
        <Form.Group className="mb-1">
          <Form.Label className="small fw-semibold">{t('modal.confirmNewPassword')}</Form.Label>
          <Form.Control type="password" value={cpConfirmPw} onChange={(e) => setCpConfirmPw(e.target.value)} placeholder={t('modal.confirmPasswordPlaceholder')} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={() => setShowChangePasswordModal(false)} disabled={cpLoading}>{t('modal.cancel')}</Button>
        <Button variant="primary" size="sm" onClick={handleChangePassword} disabled={cpLoading}>
          {cpLoading ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />{t('modal.saving')}</> : t('modal.savePassword')}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}



export default Header;
